import { EUserRole } from '@/app/[locale]/(dashboard)/dashboard/users/domain/enums/users.enum';
import { ROLE_NAVIGATION_RULES } from './config/role-navigation-rules';
import type { INavigationGuard, INavigationRoute } from './interfaces/navigation.interface';
import { logger } from '@/core/utils/logger';

/**
 * Guard de navegación centralizado
 * Maneja las reglas de acceso y redirecciones por rol
 */
export class NavigationGuard implements INavigationGuard {
  private _router: unknown;
  private readonly _organizationScopedRoles = new Set<EUserRole>([
    EUserRole.ADMIN_ORGANIZATION,
    EUserRole.USER_ORGANIZATION,
    EUserRole.AUDITOR_ORGANIZATION,
    EUserRole.EXECUTIVE_ORGANIZATION,
  ]);

  /**
   * Verifica si un rol puede acceder a una ruta específica
   * @param role Rol del usuario
   * @param route Ruta a verificar
   * @returns true si puede acceder, false en caso contrario
   */
  canAccess(role: EUserRole, route: string): boolean {
    const rules = ROLE_NAVIGATION_RULES[role];

    if (!rules) {
      logger.warn('No se encontraron reglas para el rol', { module: 'navigation-guard', role });
      return false;
    }

    // Verificar si la ruta está en las rutas prohibidas (exacta o como padre)
    const isForbidden = rules.forbiddenRoutes.some((forbiddenRoute) => route === forbiddenRoute || route.startsWith(`${forbiddenRoute}/`));
    if (isForbidden) {
      return false;
    }

    // Verificar si la ruta está en las rutas permitidas (exacta o como hija)
    return rules.allowedRoutes.some((allowedRoute) => route === allowedRoute || route.startsWith(`${allowedRoute}/`));
  }

  /**
   * Obtiene la ruta de redirección por defecto para un rol
   * @param role Rol del usuario
   * @returns Ruta de redirección por defecto
   */
  getDefaultRedirect(role: EUserRole): string {
    const rules = ROLE_NAVIGATION_RULES[role];

    if (!rules) {
      logger.warn('No se encontraron reglas para el rol', { module: 'navigation-guard', role });
      return '/dashboard/documents';
    }

    return rules.defaultRedirect;
  }

  /**
   * Obtiene todas las rutas permitidas para un rol
   * @param role Rol del usuario
   * @returns Array de rutas permitidas
   */
  getAllowedRoutes(role: EUserRole): string[] {
    const rules = ROLE_NAVIGATION_RULES[role];

    if (!rules) {
      logger.warn('No se encontraron reglas para el rol', { module: 'navigation-guard', role });
      return [];
    }

    return rules.allowedRoutes;
  }

  /**
   * Obtiene el menú de navegación para un rol
   * @param role Rol del usuario
   * @returns Array de elementos del menú de navegación
   */
  getNavigationMenu(role: EUserRole): INavigationRoute[] {
    const rules = ROLE_NAVIGATION_RULES[role];

    if (!rules) {
      logger.warn('No se encontraron reglas para el rol', { module: 'navigation-guard', role });
      return [];
    }

    return rules.navigationMenu;
  }

  /**
   * Redirige al usuario a su ruta por defecto según su rol
   * @param role Rol del usuario
   */
  redirectToDefault(role: EUserRole): void {
    const defaultRoute = this.getDefaultRedirect(role);
    // La redirección se maneja en el hook useRoleNavigation
    logger.debug('Redirecting to default route', { module: 'navigation-guard', role, defaultRoute });
  }

  /**
   * Verifica si una ruta está prohibida para un rol
   * @param role Rol del usuario
   * @param route Ruta a verificar
   * @returns true si está prohibida, false en caso contrario
   */
  isRouteForbidden(role: EUserRole, route: string): boolean {
    const rules = ROLE_NAVIGATION_RULES[role];

    if (!rules) {
      return true; // Si no hay reglas, considerar prohibida
    }

    // Verificar si la ruta está en las rutas prohibidas (exacta o como hija)
    return rules.forbiddenRoutes.some((forbiddenRoute) => route === forbiddenRoute || route.startsWith(`${forbiddenRoute}/`));
  }

  getEffectiveRoles(roles: string[], hasOrganization = true): EUserRole[] {
    const validRoles = roles.filter((role): role is EUserRole => this.isValidRole(role));

    const effectiveRoles = hasOrganization ? validRoles : validRoles.filter((role) => !this._organizationScopedRoles.has(role));

    if (effectiveRoles.length > 0) {
      return effectiveRoles;
    }

    return validRoles.includes(EUserRole.CLIENT) ? [EUserRole.CLIENT] : [];
  }

  /**
   * Obtiene el rol más alto de un array de roles
   * @param roles Array de roles del usuario
   * @returns El rol con mayor privilegio
   */
  getHighestRole(roles: string[], hasOrganization = true): EUserRole {
    const effectiveRoles = this.getEffectiveRoles(roles, hasOrganization);

    // Orden de prioridad de roles (de mayor a menor privilegio)
    const rolePriority = [
      EUserRole.OWNER,
      EUserRole.SUPER_ADMIN,
      EUserRole.SUPER_ADMIN_AUDITOR,
        EUserRole.ADMIN_ORGANIZATION,
      EUserRole.USER_ORGANIZATION,
      EUserRole.AUDITOR_ORGANIZATION,
      EUserRole.EXECUTIVE_ORGANIZATION,
      EUserRole.CLIENT,
    ];

    // Encontrar el rol con mayor prioridad
    for (const priorityRole of rolePriority) {
      if (effectiveRoles.includes(priorityRole)) {
        return priorityRole;
      }
    }

    // Si no se encuentra ningún rol válido, retornar CLIENT
    return EUserRole.CLIENT;
  }

  /**
   * Valida si un rol es válido
   * @param role Rol a validar
   * @returns true si es válido, false en caso contrario
   */
  isValidRole(role: string): role is EUserRole {
    return Object.values(EUserRole).includes(role as EUserRole);
  }
}

/**
 * Hook para usar el guard de navegación
 */
export const useNavigationGuard = () => {
  return new NavigationGuard();
};
